import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export enum FeedbackScreen {
	RATING = 'rating',
	THANKYOU = 'thankyou',
	LOW = 'low',
	DONE = 'done',
}

export interface FeedbackSubmission {
	rating: number;
	comment: string;
	contact: string;
}

@Component({
	selector: 'app-feedback-widget',
	standalone: false,
	templateUrl: './feedback-widget.component.html',
	styleUrls: ['./feedback-widget.component.scss'],
})
export class FeedbackWidgetComponent {
	@Input() badge = 'TOPGOLF';
	@Input() googleUrl = 'https://www.google.com/search?q=Topgolf+Wien+Reviews#lrd=0x476daf001abfe001:0x862458af752e8b5a,3,,,,';
	// When provided, overrides the translated rating labels. Index 0 is unused (ratings are 1-5).
	@Input() labels: string[] | null = null;

	// Context used when posting low-rating feedback to the Smoothr webhook.
	@Input() venueReadableId: string | null = null;
	@Input() orderId: string | null = null;
	@Input() orderCode: string | null = null;
	@Input() paymentId: string | null = null;
	@Input() participantId: string | null = null;

	@Output() ratingSelected = new EventEmitter<number>();
	@Output() lowFeedbackSubmitted = new EventEmitter<FeedbackSubmission>();
	@Output() googleHandoff = new EventEmitter<{ rating: number; accepted: boolean }>();
	readonly stars = [1, 2, 3, 4, 5];
	readonly FeedbackScreen = FeedbackScreen;
	screen: FeedbackScreen = FeedbackScreen.RATING;
	selected = 0;
	hovered = 0;
	comment = '';
	contact = '';
	submitting = false;
	submitError = '';
	private http = inject(HttpClient);
	private translate = inject(TranslateService);

	get activeStarCount(): number {
		return this.hovered || this.selected;
	}

	get currentLabel(): string {
		const index = this.activeStarCount;
		if (!index) {
			return '';
		}
		return this.labels?.[index] ?? this.translate.instant(`feedbackWidget.labels.${index}`);
	}

	starColor(index: number): string {
		return index <= this.activeStarCount ? '#0070ff' : 'rgba(255, 255, 255, 0.2)';
	}

	onStarHover(index: number): void {
		this.hovered = index;
	}

	onStarLeave(): void {
		this.hovered = 0;
	}

	onStarClick(index: number): void {
		this.selected = index;
		this.hovered = 0;
		this.ratingSelected.emit(index);

		if (index === 5) {
			this.sendToWebhook('').catch(err => console.error('Feedback webhook failed', err));
		}

		setTimeout(() => {
			this.screen = index === 5 ? FeedbackScreen.THANKYOU : FeedbackScreen.LOW;
		}, 300);
	}

	goGoogle(): void {
		this.googleHandoff.emit({ rating: this.selected, accepted: true });
		window.location.href = this.googleUrl;
	}

	showDone(): void {
		this.googleHandoff.emit({ rating: this.selected, accepted: false });
		this.screen = FeedbackScreen.DONE;
	}

	async submitLowFeedback(): Promise<void> {
		const trimmed = this.comment.trim();
		const submission: FeedbackSubmission = { rating: this.selected, comment: trimmed, contact: this.contact.trim() };
		this.lowFeedbackSubmitted.emit(submission);

		this.submitting = true;
		this.submitError = '';

		try {
			await this.sendToWebhook(trimmed);
			this.screen = FeedbackScreen.DONE;
		} catch (err) {
			console.error('Feedback webhook failed', err);
			this.submitError = this.translate.instant('feedbackWidget.submitError');
		} finally {
			this.submitting = false;
		}
	}

	reset(): void {
		this.selected = 0;
		this.hovered = 0;
		this.comment = '';
		this.contact = '';
		this.submitError = '';
		this.screen = FeedbackScreen.RATING;
	}

	private async sendToWebhook(comment: string): Promise<void> {
		const venue = this.venueReadableId?.trim() || 'unknown';
		const reference = this.paymentId?.trim() || this.orderId?.trim() || 'unknown';
		const message = this.buildMessage(comment);
		const contact = this.contact.trim().slice(0, 100);

		let params = new HttpParams()
			.set('venue', venue)
			.set('message', message)
			.set('rating', String(this.selected))
			.set('reference', reference);

		if (contact) {
			params = params.set('contact', contact);
		}

		await firstValueFrom(
			this.http.get(environment.smoothrFeedbackApiUrl, {
				params,
				headers: { 'X-API-Key': environment.smoothrFeedbackApiKey },
			})
		);
	}

	private buildMessage(comment: string): string {
		const meta: string[] = [];
		if (this.orderId) meta.push(`Order ID: ${this.orderId}`);
		if (this.orderCode) meta.push(`Order Code: ${this.orderCode}`);
		if (this.participantId) meta.push(`Participant ID: ${this.participantId}`);

		const header = meta.join(' | ');
		const body = comment ? `\n---\n${comment}` : `\n---\n${this.translate.instant('feedbackWidget.noComment')}`;
		const combined = header ? `${header}${body}` : body.replace(/^\n---\n/, '');
		return combined.length > 1000 ? combined.slice(0, 1000) : combined;
	}
}
